<template>
  <div class="border-2 p-4 transition-all duration-200" :class="cardClasses" @click="handleClick">
    <div class="mb-3 flex items-start justify-between">
      <div class="flex items-center">
        <div class="mr-3 flex h-8 w-8 items-center justify-center" :class="iconContainerClasses">
          <component :is="analysis.iconComponent" v-if="analysis.iconComponent" class="h-5 w-5" :class="iconClasses" />
          <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" :class="iconClasses">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="analysis.icon"></path>
          </svg>
        </div>
        <div>
          <h5 class="font-semibold" :class="titleClasses">
            {{ analysis.title }}
          </h5>
          <p class="text-sm" :class="descriptionClasses">
            {{ analysis.description }}
          </p>
        </div>
      </div>
      <div class="flex items-center">
        <div class="flex h-5 w-5 items-center justify-center border-2 transition-colors" :class="checkboxClasses">
          <svg v-if="selected && !disabled" class="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"></path>
          </svg>
        </div>
      </div>
    </div>
    <p class="text-xs" :class="longDescriptionClasses">
      {{ analysis.longDescription }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'

interface AnalysisConfig {
  id: string
  title: string
  description: string
  longDescription: string
  icon?: string
  iconComponent?: Component
  color: string
}

interface Props {
  analysis: AnalysisConfig
  selected: boolean
  disabled: boolean
}

interface Emits {
  (e: 'click'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const cardClasses = computed(() => {
  if (props.disabled) {
    return 'border-gray-200  cursor-not-allowed opacity-95'
  }

  const baseClasses = 'cursor-pointer'
  const colorClasses = {
    blue: props.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md',
    green: props.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md',
    purple: props.selected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md',
    orange: props.selected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md',
  }

  return `${baseClasses} ${colorClasses[props.analysis.color as keyof typeof colorClasses]}`
})

const iconContainerClasses = computed(() => {
  if (props.disabled) {
    return 'bg-gray-100'
  }

  const colorClasses = {
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    purple: 'bg-purple-100',
    orange: 'bg-orange-100',
  }

  return colorClasses[props.analysis.color as keyof typeof colorClasses]
})

const iconClasses = computed(() => {
  if (props.disabled) {
    return 'text-gray-400'
  }

  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
  }

  return colorClasses[props.analysis.color as keyof typeof colorClasses]
})

const titleClasses = computed(() => {
  if (props.disabled) {
    return 'text-gray-400'
  }
  return 'text-gray-800'
})

const descriptionClasses = computed(() => {
  if (props.disabled) {
    return 'text-gray-400'
  }
  return 'text-gray-600'
})

const longDescriptionClasses = computed(() => {
  if (props.disabled) {
    return 'text-gray-400'
  }
  return 'text-gray-500'
})

const checkboxClasses = computed(() => {
  if (props.disabled) {
    return 'border-gray-300 bg-gray-200'
  }

  const colorClasses = {
    blue: props.selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300',
    green: props.selected ? 'border-green-500 bg-green-500' : 'border-gray-300',
    purple: props.selected ? 'border-purple-500 bg-purple-500' : 'border-gray-300',
    orange: props.selected ? 'border-orange-500 bg-orange-500' : 'border-gray-300',
  }

  return colorClasses[props.analysis.color as keyof typeof colorClasses]
})

const handleClick = () => {
  if (!props.disabled) {
    emit('click')
  }
}
</script>

<style scoped>
.cursor-pointer:hover {
  transform: translateY(-2px);
}

.w-5.h-5 {
  transition: all 0.2s ease;
}
</style>
